<template>
  <div class="cr-page ak-page">
    <SnackbarComp :SnackbarComponent="SnackbarComponent" />

    <div class="cr-panel ak-panel">
      <div class="ak-panel__scroll">
        <OrgCredentialsPanel
          ref="credentialsPanel"
          :api-key="orgApiKey"
          :oauth-client-id="oauthClientId"
          :oauth-client-secret="oauthClientSecret"
          :oauth-token-url="oauthTokenUrl"
          :generating-api-key="generatingApiKey"
          :generating-oauth="generatingOAuth"
          :refreshing="refreshing"
          :disabled="!isSessionReady"
          @generate-api-key="generateApiKey"
          @generate-oauth="generateOAuthClient"
          @refresh="handleRefresh"
          @copied="onCopied"
          @copy-error="onCopyError"
        />

        <ApiDocsLaunchSection
          :disabled="!isSessionReady"
          @open="openDocs"
        />
      </div>
    </div>

    <ApiDocsFullscreenViewer
      v-model="docsOpen"
      :doc-source="docsSource"
      :api-key="orgApiKey"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import ApiDocsLaunchSection from '@/components/ApiKeys/ApiDocsLaunchSection.vue'
import ApiDocsFullscreenViewer from '@/components/ApiKeys/ApiDocsFullscreenViewer.vue'
import OrgCredentialsPanel from '@/components/ApiKeys/OrgCredentialsPanel.vue'
import apiKeysApi from '@/mixins/ApiKeys/apiKeysApi'
import organizationDisplayMixin from '@/mixins/Organization/organizationDisplayMixin'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import {
  mergeOrgCredentialUpdate,
  pickOrgApiKey,
  pickOrgOAuthClient,
} from '@/utils/orgCredentials'
import { getPortalEnvironment } from '@/config/portalEnvironment'

export default {
  name: 'ApiKeysPage',
  components: {
    SnackbarComp,
    ApiDocsLaunchSection,
    ApiDocsFullscreenViewer,
    OrgCredentialsPanel,
  },
  mixins: [apiKeysApi, organizationDisplayMixin],
  computed: {
    ...mapGetters(['sessionContext']),
    orgCredentials() {
      return pickOrgOAuthClient(this.displayOrganization)
    },
    orgApiKey() {
      // This org's own key when it has one, otherwise the environment-wide key
      // it shares with every other org. The fallback matters during rollout:
      // orgs created before per-org keys existed keep working until they
      // regenerate, rather than seeing an empty card.
      return pickOrgApiKey(this.displayOrganization) || getPortalEnvironment().publicApiKey || ''
    },
    oauthClientId() {
      return this.orgCredentials.clientId
    },
    oauthClientSecret() {
      return this.orgCredentials.clientSecret
    },
    oauthTokenUrl() {
      return this.orgCredentials.tokenUrl
    },
  },
  data() {
    return {
      SnackbarComponent: {},
      generatingApiKey: false,
      generatingOAuth: false,
      refreshing: false,
      docsOpen: false,
      docsSource: 'public',
    }
  },
  methods: {
    openDocs(source = 'public') {
      this.docsSource = source === 'contact' ? 'contact' : 'public'
      this.docsOpen = true
    },
    showSnack(color, message) {
      this.SnackbarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: color,
        SnackbarText: message,
      }
    },
    showError(payload) {
      this.showSnack('error', getApiMessage(payload, 'Request failed'))
    },
    onCopied(message = 'Copied to clipboard') {
      this.showSnack('success', message)
    },
    onCopyError() {
      this.showSnack('error', 'Failed to copy')
    },
    applyCredentialResponse(response) {
      const merged = mergeOrgCredentialUpdate(
        response,
        this.displayOrganization,
      )
      if (merged && typeof merged === 'object') {
        this.$store.commit('UPDATE_ORGANIZATION', merged)
      }
    },
    async refreshCredentialsAfterGenerate() {
      try {
        await this.refreshOrganizationFromApi()
      } catch (err) {
        this.showSnack(
          'warning',
          getApiMessage(err, 'Generated, but failed to refresh details.'),
        )
      }
    },
    async handleRefresh() {
      if (!this.isSessionReady) return

      this.refreshing = true
      try {
        await this.refreshOrganizationFromApi()
      } catch (err) {
        this.showError(err)
      } finally {
        this.refreshing = false
      }
    },
    async generateApiKey() {
      if (!this.isSessionReady) {
        this.showError({ message: 'Session is not ready.' })
        return
      }

      this.generatingApiKey = true
      try {
        const response = await this.generateOrgApiKeyRecord({})
        if (!isApiSuccess(response)) {
          this.showError(response)
          return
        }

        this.applyCredentialResponse(response)
        await this.refreshCredentialsAfterGenerate()
        this.showSnack('success', 'API key generated successfully.')
      } catch (err) {
        this.showError(err)
      } finally {
        this.generatingApiKey = false
      }
    },
    async generateOAuthClient() {
      if (!this.isSessionReady) {
        this.showError({ message: 'Session is not ready.' })
        return
      }

      this.generatingOAuth = true
      try {
        const response = await this.generateOrgOAuthClientRecord({})
        if (!isApiSuccess(response)) {
          this.showError(response)
          return
        }

        this.applyCredentialResponse(response)
        await this.refreshCredentialsAfterGenerate()
        this.showSnack('success', 'OAuth client generated successfully.')
      } catch (err) {
        this.showError(err)
      } finally {
        this.generatingOAuth = false
      }
    },
  },
}
</script>
